import React, { useState, useEffect } from "react";
import { Table, UncontrolledDropdown, DropdownToggle, DropdownItem, DropdownMenu, Button } from "reactstrap";
import PropTypes, { shape } from "prop-types";

import style from "../../Grid.module.scss";
import { getAccountType } from "../types";
import ModalConfirm from "../../Modal/ModalConfirm";
import ModalInstruction from "../../Modal/ModalInstruction";
import { CircularProgress } from "../../../..";
import { showToast } from "../../../../utils/Helper";

function generateEmptyInstruction() {
  return {
    AccountInstructionType: "",
    AccountInstructionsCurrency: "",
    AccountInstructionAssetid: "",
    AccountInstructionTarget: "",
    AccountInstructionBankAccountItemId: "",
    AccountInstructionMin: "",
    AccountInstructionMax: "",
  };
}

const InstructionsTab = ({
  accountId,
  accountOnb,
  instructions,
  setAccountData,
  services,
  currencies,
  isOnboarding,
  userType,
  newOnboardingVersion = false,
}) => {
  const [fetching, setFetching] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [editMode, setEditMode] = useState(false);

  const [modalInstruction, setModalInstruction] = useState({
    index: -1,
    open: false,
    saving: false,
    instruction: generateEmptyInstruction(),
  });

  const onChangeEdit = (name, value, strValue,strValue2) => {
    if (name == "AccountInstructionAssetId" && value) {
      if (strValue != modalInstruction.instruction.AccountInstructionCurrencyISO) {
        showToast("Currency must be equal to the asset currency", true);
        return;
      } else {
        let instructionUpd = { ...modalInstruction.instruction, [name]: value};
        if (strValue2 && name == "AccountInstructionAssetId") {
          instructionUpd = { ...instructionUpd, AccountInstructionAssetticker: strValue2 };
        }
        setModalInstruction({
          ...modalInstruction,
          instruction: instructionUpd,
        });
      }
    } else {
      let instructionUpd = { ...modalInstruction.instruction, [name]: value };
      if (name === "AccountInstructionsCurrency") {
        instructionUpd = { ...instructionUpd, AccountInstructionCurrencyISO: strValue };
      }
      setModalInstruction({
        ...modalInstruction,
        instruction: instructionUpd,
      });
    }
  };

  const toggleModalInstructions = (instruction, index) => {
    setModalInstruction({
      index: index,
      saving: modalInstruction.saving,
      open: !modalInstruction.open,
      instruction: instruction ? instruction : generateEmptyInstruction(),
    });
  };

  const handleEdit = (index) => {
    setEditMode(true);
    toggleModalInstructions(instructions[index], index);
  };

  const handleAdd = () => {
    setEditMode(false);
    toggleModalInstructions();
  };

  const handleDelete = (index) => {
    const id = instructions[index].RelatedAccountId;
    const name = instructions[index].RelatedAccountName;

    let instructionToDelete = instructions[index];
    const account = {
      AccountId: accountId,
      Instructions: [
        {
          ...instructionToDelete,
          DeleteInstruction: true,
        },
      ],
      isOnboardingAccount: newOnboardingVersion ? true : false,
    };
    const body = {
      account,
      accountChangeType: getAccountType.Instructions,
    };

    setModalInstruction({ ...modalInstruction, saving: true });
    if (!isOnboarding) {
      services.updateAccountInstructon(body, (response) => {
        if (response && response.result && response.result.Code === 200) {
          if (userType != "Advisor") {
            let updatedInstructions = instructions.filter((_, i) => i !== index);
            account.Instructions = updatedInstructions;
            showToast("Changes saved successfully.");
            setAccountData(account, false);
          } else {
            showToast("Changes were sent for approval.");
          }
          setModalInstruction({
            saving: false,
            open: false,
            instruction: generateEmptyInstruction(),
          });
        } else {
          setModalInstruction({
            ...modalInstruction,
            saving: false,
          });
        }
      });
    } else {
      let updatedInstructions = instructions.filter((_, i) => i !== index);
      let accountOnboarding = {
        ...accountOnb,
        Instructions: updatedInstructions,
      };

      services.fetchNewAccount(
        { account: accountOnboarding, accountChangeType: "Instructions" },
        (response) => {
          if (response && response.result && response.result.Code === 200) {
            setAccountData(accountOnboarding, true);
            setModalInstruction({
              saving: false,
              open: false,
              instruction: generateEmptyInstruction(),
            });
            showToast("Instruction deleted successfully");
          }
        }
      );
    }
  };

  const handleSaveInstruction = () => {
    //check if the instruction already exists
    let exists = false;
    let index = 0;
    if (instructions) {
      instructions.forEach((ins) => {
        if (
          modalInstruction.instruction.AccountInstructionsCurrency === ins.AccountInstructionsCurrency &&
          modalInstruction.instruction.AccountInstructionType === ins.AccountInstructionType
        ) {
          if (modalInstruction.index !== index) {
            showToast("Instruction with this type and currency already exists", true);
            exists = true;
            return;
          }
        }
        index++;
      });
    }

    if (
      !modalInstruction.instruction.AccountInstructionAssetId &&
      modalInstruction.instruction.AccountInstructionTarget == "Reinvest"
    ) {
      showToast("An asset with the same currency is required to create the instruction.", true);
      return;
    }

    if (exists) {
      return;
    }

    const newInstruction = { ...modalInstruction.instruction };
    const account = {
      AccountId: accountId,
      Instructions: [
        {
          ...newInstruction,
          DeleteInstruction: false,
        },
      ],
      isOnboardingAccount: newOnboardingVersion ? true : false,
    };
    let body = {
      account,
      accountChangeType: getAccountType.Instructions,
    };

    setModalInstruction({ ...modalInstruction, saving: true });

    if (!isOnboarding) {
      services.updateAccountInstructon(body, (response) => {
        if (response && response.result && response.result.Code === 200) {
          if (userType != "Advisor") {
            if (editMode) {
              let updatedInstructions = instructions.map((ins, i) => {
                if (i === modalInstruction.index) {
                  return newInstruction;
                }
                return ins;
              });
              account.Instructions = updatedInstructions;
            } else {
              account.Instructions = [...(instructions || []), newInstruction];
            }
            showToast("Changes saved successfully.");
            setAccountData(account, false);
            setModalInstruction({
              saving: false,
              open: false,
              instruction: generateEmptyInstruction(),
            });
          } else {
            showToast("Changes were sent for approval.");
            setModalInstruction({
              ...modalInstruction,
              saving: false,
              open: false,
              instruction: generateEmptyInstruction(),
            });
          }
        } else {
          setModalInstruction({
            ...modalInstruction,
            saving: false,
          });
        }
      });
    } else {
      let updatedInstructions = [];
      if (editMode) {
        updatedInstructions = instructions.map((ins, i) => {
          if (i === modalInstruction.index) {
            return newInstruction;
          }
          return ins;
        });
      } else {
        updatedInstructions = [...(instructions || []), newInstruction];
      }
      let accountOnboarding = {
        ...accountOnb,
        Instructions: updatedInstructions,
      };

      services.fetchNewAccount(
        { account: accountOnboarding, accountChangeType: "Instructions" },
        (response) => {
          if (response && response.result && response.result.Code === 200) {
            setAccountData(accountOnboarding, true);
            setModalInstruction({
              saving: false,
              open: false,
              instruction: generateEmptyInstruction(),
            });
            showToast("Changes saved successfully.");
          }
        }
      );
    }
  };

  useEffect(() => {
    if (!isOnboarding) {
      const body = {
        AccountGetInfo: {
          GetAccountDataId: accountId,
          GetAccountDataType: getAccountType.BankAccounts,
          InstructionsBankAccountActive: true,
        },
      };
      services.fetchAccountSectorInfo(body, (response) => {
        if (response && response.SDTAccount) {
          setBankAccounts(response.SDTAccount.BankAccounts);
        }
      });
    } else {
      const body = {
        onboardingProcessId: accountId,
      };
      services.fetchBankAccounts(body, (response) => {
        if (response && response.bankAccounts) {
          setBankAccounts(response.bankAccounts);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (!isOnboarding) {
      if (!instructions && !fetching) {
        const body = {
          AccountGetInfo: {
            GetAccountDataId: accountId,
            GetAccountDataType: getAccountType.Instructions,
          },
        };
        services.fetchAccountSectorInfo(body, (response) => {
          if (response && response.SDTAccount) {
            if (response.SDTAccount.Instructions) {
              setAccountData(
                {
                  AccountId: accountId,
                  Instructions: response.SDTAccount.Instructions,
                },
                false
              );
            }
          }
          setFetching(false);
        });
        setFetching(true);
      }
    } else {
      if (!instructions && !fetching){
      services.fetchNewAccountInfo(
        { AccountGetInfo: { GetAccountDataOnboardingId: accountId, GetAccountDataType: "Instructions" } },
        (response) => {
          if (response && response.SDTAccount) {
            setAccountData(
              {
                AccountId: accountId,
                Instructions: response.SDTAccount.Instructions ? response.SDTAccount.Instructions : [],
              },
              false
            );
          }
          setFetching(false);
        }
      );
    }
    }
  }, [setAccountData]);

  return (
    <div>
      <Table className={style.table}>
        <thead>
          <tr>
            <th style={{ maxWidth: 50 }}> </th>
            <th>Type</th>
            <th>Currency</th>
            <th>Target</th>
          </tr>
        </thead>
        <tbody>
          {fetching ? (
            <tr>
              <td colSpan={5}>
                <CircularProgress sm />
              </td>
            </tr>
          ) : (
            <React.Fragment>
              {instructions && instructions.length > 0 ? (
                instructions.map((ins, i) => (
                  <tr key={ins.RelatedAccountId}>
                    <td style={{ width: 50 }}>
                      <RenderDropdownActions index={i} onDelete={handleDelete} onEdit={handleEdit} />
                    </td>
                    <td onClick={() => handleEdit(i)}>{ins.AccountInstructionType}</td>
                    <td onClick={() => handleEdit(i)}>{ins.AccountInstructionCurrencyISO}</td>
                    <td onClick={() => handleEdit(i)}>{ins.AccountInstructionTarget}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center" }}>
                    No Result.
                  </td>
                </tr>
              )}
            </React.Fragment>
          )}
        </tbody>
      </Table>
      <Button color="primary" onClick={handleAdd}>
        <i className="fa fa-plus" /> Add Instruction
      </Button>
      <ModalInstruction
        {...modalInstruction}
        currencies={currencies}
        toggle={toggleModalInstructions}
        onChange={onChangeEdit}
        onSave={handleSaveInstruction}
        bankAccounts={bankAccounts}
        services={services}
        saving={modalInstruction.saving}
        editMode={editMode}
      />
    </div>
  );
};

InstructionsTab.propTypes = {
  accountId: PropTypes.string.isRequired,
  instructions: PropTypes.array.isRequired,
  setAccountData: PropTypes.func.isRequired,
  services: PropTypes.object.isRequired,
  currencies: PropTypes.array,
};

InstructionsTab.defaultProps = {
  currencies: undefined,
};

function RenderDropdownActions({ onEdit, onDelete, index }) {
  return (
    <UncontrolledDropdown>
      <DropdownToggle color="link" size="lg" className={style.dropdown}>
        <i className="icon-options-vertical icons d-block" />
      </DropdownToggle>
      <DropdownMenu>
        <DropdownItem onClick={() => onEdit(index)}>
          <i className="fa fa-edit" />
          Edit
        </DropdownItem>
        <DropdownItem onClick={() => onDelete(index)}>
          <i className="fa fa-trash" />
          Delete
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  );
}

RenderDropdownActions.propTypes = {
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
};

export default InstructionsTab;
